import { Component } from "global/interfaces/component.interface";
import ControllerBase from "global/js/ControllerBase";
import { BehaviorSubject, fromEvent } from "rxjs";
import { map } from "rxjs/operators";
import ControllerToggleSearchCategory, { MoleculeToggleCategoryEvents } from "wolf/components/molecules/m-category-toggle-selector/m-category-toggle-selector.controller";
import { MoleculeInputSearchFatEvent, MoleculeInputSearchFatEvents } from "wolf/components/molecules/m-input-search-fat/m-input-search-fat.events";
import { MoleculeTeaserPostProps } from "wolf/components/molecules/m-teaser-post/m-teaser-post.data";
import { MoleculePaginationProps } from "wolf/components/molecules/m-pagination/m-pagination.data";
import { MoleculePaginationEvent, MyMoleculePaginationEvents } from "wolf/components/molecules/m-pagination/m-pagination.events";
import Controller from 'wolf/global/js/controller';
import { MoleculeCategoryToggleSelectorProps, VIEW_ALL_RESULTS } from "wolf/components/molecules/m-category-toggle-selector/m-category-toggle-selector.data";
import { MoleculePaginationBuilder } from "wolf/components/molecules/m-pagination/m-pagination.data-builder";
import ControllerProxy from "global/js/ControllerProxy";
import { AtomCopyProps } from "../../atoms/a-copy/a-copy.data";

// Pagination template
const searchResultsPaginationTemplate = require('components/molecules/m-pagination/m-pagination.template.twig') as (
	data: Component<MoleculePaginationProps>
) => string;

// Category template
const searchCategoryTemplate = require('components/molecules/m-category-toggle-selector/m-category-toggle-selector.template.twig') as (
	data: Component<MoleculeCategoryToggleSelectorProps>
) => string;

const teaserPostTemplate = require('components/molecules/m-teaser-post/m-teaser-post.template.twig') as (
	data: Component<MoleculeTeaserPostProps>,
) => string;

export interface SearchResultsStructure {
	totalItems: number;
	posts?: MoleculeTeaserPostProps[];
	pagination?: MoleculePaginationProps;
	categoryToggler: MoleculeCategoryToggleSelectorProps;
	noResultsText?: AtomCopyProps;
}

interface ComponentRefs {
	categoryTogglerWrapper: 'categoryTogglerWrapper'
	paginationWrapper: 'paginationWrapper';
	totalResultsWrapper: 'totalResultsWrapper';
	resultsWrapper: 'resultsWrapper';
}

export default class ControllerSearchResults extends ControllerBase<ComponentRefs> {
	private searchUrl: string;
	private searchInput: HTMLInputElement;
	private searchResultsStructure: SearchResultsStructure;
	private totalItems: HTMLSpanElement;
	private pagination: HTMLDivElement;
	private categoryTogglerWrapper: HTMLDivElement;
	private selectedCategory: string
	private searchValue: string
	private categoryTogglerController?: ControllerToggleSearchCategory
	private resultsPerPage: number
	private headline: HTMLDivElement;
	private headlineNoResults: HTMLDivElement;
	private headlineNoResultsText: HTMLDivElement;

	/** An observable of the current page, updated on change */
	private onPageChange: BehaviorSubject<number | null>;

	constructor(element: HTMLElement) {
		super(element)
		this.searchUrl = this.$element.dataset.searchurl as string
		this.totalItems = this.$references.totalResultsWrapper as HTMLSpanElement;
		this.pagination = this.$references.paginationWrapper as HTMLDivElement;
		this.categoryTogglerWrapper = this.$references.categoryTogglerWrapper as HTMLDivElement;
		this.searchInput = this.$element.querySelector('.js-search-bar .m-input-search-fat__input') as HTMLInputElement;
		this.resultsPerPage = this.$element.dataset.resultsperpage ? +this.$element.dataset.resultsperpage : 5 as number
		this.onPageChange = new BehaviorSubject(null);
		this.headline = this.$element.querySelector('.o-search-results__headline') as HTMLDivElement;
		this.headlineNoResults = this.$element.querySelector('.o-search-results__headline_no_results') as HTMLDivElement;
		this.headlineNoResultsText = this.$element.querySelector('.o-search-results__headline_no_results .a-copy') as HTMLDivElement;

		this.init();
	}

	private init(): void {
		this.handleSubmit();
		this.registerEvents();
		this.searchValue = this.searchInput.value;
		// react to page changes
		this.onPageChange.subscribe((newPage) => {
			if (newPage) {

				this.search([
					['search', this.searchValue],
					['category', this.selectedCategory],
					['resultsPerPage', this.resultsPerPage],
					['page', newPage]
				])
			}
		});

		this.observePaginationEvents();
	}

	/**
	 * Listen for clicks on any of the pagination pages
	 * @returns void
	 */
	private observePaginationEvents(): void {
		fromEvent(window, MyMoleculePaginationEvents.pageEvent)
			.pipe(
				map((event: CustomEvent<MoleculePaginationEvent>) => {
					return event.detail.page || -1;
				}),
			)
			.subscribe((e) => {
				this.onPageChange.next(e);
			});
	}

	private handleSubmit() {
		fromEvent(window, MoleculeInputSearchFatEvents.search)
			.pipe(
				map((event: CustomEvent<MoleculeInputSearchFatEvent>) => {
					this.searchValue = event.detail.searchValue
					return this.searchValue
				})
			)
			.subscribe((e) => {
				this.search([
					['search', this.searchValue],
					['category', this.selectedCategory],
					['resultsPerPage', this.resultsPerPage]
				])

			})
	}

	private renderContent(data: SearchResultsStructure) {

		this.totalItems.innerHTML = data.totalItems.toString()

		if (data.posts) {
			let content = ''
			data.posts.forEach((post) => {
				content += teaserPostTemplate({ data: post })
			})

			const resultsRef: ComponentRefs['resultsWrapper'] = "resultsWrapper"
			this.domUtils.renderDomElement(resultsRef, content);
		}

		if (data.totalItems > 0) {
			this.headline.style.display = 'inline-flex';
			this.headlineNoResults.style.display = 'none';
		} else {
			if (data.noResultsText) {
				this.headlineNoResultsText.innerHTML = data.noResultsText.text.toString()
			}

			this.headlineNoResults.style.display = 'block';
			this.headline.style.display = 'none';
		}

		// Render of pagination in front
		if (data.pagination) {

			const newPaginationSettings = Object.assign({}, data.pagination.paginationSettings);

			const newPagination: MoleculePaginationProps = {
				...data.pagination,
				paginationSettings: newPaginationSettings,
			};

			const paginationData = new MoleculePaginationBuilder(newPagination);

			const paginationHTML = searchResultsPaginationTemplate({
				data: paginationData.data
			})

			const paginationRef: ComponentRefs['paginationWrapper'] = "paginationWrapper"

			this.domUtils.renderDomElement(paginationRef, paginationHTML)
			this.initComponents(this.pagination)

		} else {
			this.pagination.innerHTML = ''
		}

		// Render of categoryToggler in fron
		if (data.categoryToggler) {
			const categoryTogglerHTML = searchCategoryTemplate({
				data: data.categoryToggler
			});

			const categoryRef: ComponentRefs['categoryTogglerWrapper'] = 'categoryTogglerWrapper';
			this.domUtils.renderDomElement(categoryRef, categoryTogglerHTML);
			this.initComponents(this.categoryTogglerWrapper)

		} else {
			this.categoryTogglerWrapper.innerHTML = ''
			this.categoryTogglerController = undefined
		}
	}

	private async search(searchQueryParams: [string, any][]) {
		const url = new URL(this.searchUrl);

		searchQueryParams.forEach(([key, value]) => {
			if (value) url.searchParams.append(key, value);
		})

		await fetch(url.toString())
			.then(response => response.json())
			.then((response) => {
				this.searchResultsStructure = response.data
				this.renderContent(this.searchResultsStructure)
			})
			.catch((err) => {
				console.error(err);
			})
	}

	private fetchOnCategoryChange = (event: CustomEvent) => {
		this.selectedCategory = event.detail.category;
		this.search([
			['search', this.searchValue],
			['category', this.selectedCategory],
			['resultsPerPage', this.resultsPerPage]
		])
	}

	private registerEvents() {
		window.addEventListener(MoleculeToggleCategoryEvents.UPDATE_FILTER_CATEGORY, this.fetchOnCategoryChange)
	}

	private initComponents(wrapper) {
		const components = wrapper.querySelectorAll('[data-controller]');

		components.forEach((el) => {
			const componentName = el.dataset.controller;
			new ControllerProxy(Controller, componentName, el);
		});
	}

}
